import {
  BOARD_PROCESS_WAIT_TIME_MS,
} from "../constants/boardProcessConsts";

const STATUS_KEY_PATTERN = /(estado|status|procesando|processing|resultado|result|valor|value|bit)/i;
const DATE_KEY_PATTERN = /(fecha|date)/i;
const SYNC_DATE_KEY_PATTERN = /(sincron|ultim|proceso|actualiz|refresh|procesad)/i;
const START_DATE_KEY_PATTERN = /(inicio|start|comienzo|desde)/i;

const isNumberValue = (value: unknown): value is number =>
  typeof value === "number" && !isNaN(value);

const toBit = (value: unknown): number | null => {
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }
  if (isNumberValue(value)) {
    return value === 0 ? 0 : 1;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "0" || normalized === "false") {
      return 0;
    }
    if (normalized === "1" || normalized === "true") {
      return 1;
    }
    const parsed = Number(normalized);
    if (!isNaN(parsed)) {
      return parsed === 0 ? 0 : 1;
    }
  }
  return null;
};

const toIsoDate = (value: unknown): string | null => {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (typeof value === "number") {
    if (!isFinite(value) || value === 0 || value === 1) {
      return null;
    }
    const numericDate = new Date(value);
    return isNaN(numericDate.getTime()) ? null : numericDate.toISOString();
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "0" || trimmed === "1") {
      return null;
    }
    const parsed = new Date(trimmed);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }
  return null;
};

export const toBoardProcessRecord = (raw: any): Record<string, any> | null => {
  if (Array.isArray(raw)) {
    return raw.length && typeof raw[0] === "object" ? raw[0] : null;
  }
  if (raw && typeof raw === "object") {
    if ("data" in raw) {
      const inner = raw.data;
      if (Array.isArray(inner)) {
        return inner.length && typeof inner[0] === "object" ? inner[0] : null;
      }
      return inner && typeof inner === "object" ? inner : null;
    }
    return raw;
  }
  return null;
};

export const parseBoardProcessStatus = (raw: any): number | null => {
  const record = toBoardProcessRecord(raw);
  if (!record) {
    return null;
  }
  if (isNumberValue(record.Estado)) {
    return toBit(record.Estado);
  }
  const key = Object.keys(record).find((item) => STATUS_KEY_PATTERN.test(item));
  return key ? toBit(record[key]) : null;
};

export const extractBoardProcessDate = (record: Record<string, any> | null): string | null => {
  if (!record) {
    return null;
  }
  const entries = Object.entries(record);
  const isCandidate = (key: string): boolean =>
    DATE_KEY_PATTERN.test(key) && !START_DATE_KEY_PATTERN.test(key);

  for (const [key, value] of entries) {
    if (!isCandidate(key) || !SYNC_DATE_KEY_PATTERN.test(key)) {
      continue;
    }
    const isoDate = toIsoDate(value);
    if (isoDate) {
      return isoDate;
    }
  }

  for (const [key, value] of entries) {
    if (!isCandidate(key)) {
      continue;
    }
    const isoDate = toIsoDate(value);
    if (isoDate) {
      return isoDate;
    }
  }

  return null;
};

const pad = (value: number): string => value.toString().padStart(2, "0");

export const formatBoardProcessDateTime = (value?: string | Date | null): string => {
  if (!value) {
    return "-";
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return "-";
  }
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

export const getRemainingBoardProcessWaitMs = (
  processedAt?: string | null,
  now: number = Date.now(),
): number => {
  if (!processedAt) {
    return 0;
  }
  const processedTime = new Date(processedAt).getTime();
  if (isNaN(processedTime)) {
    return 0;
  }
  return Math.max(0, BOARD_PROCESS_WAIT_TIME_MS - (now - processedTime));
};

export const formatBoardProcessWaitTime = (remainingMs: number): string =>
  remainingMs <= 0 ? "0 min" : `${Math.ceil(remainingMs / 60000)} min`;
